import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Loader2, Check, CheckCheck, TrendingUp, Radio, AlertTriangle, Settings, ExternalLink } from 'lucide-react';
import { apiGet, apiPost, apiPatch } from '../data/api';
import t from '../i18n/it';

const TYPE_CONFIG = {
  prematch: {
    icon: TrendingUp,
    label: t.notifications.type_prematch,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  live: {
    icon: Radio,
    label: t.notifications.type_live,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
  },
  system: {
    icon: Settings,
    label: t.notifications.type_system,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
  },
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 60000;
  if (diff < 1) return t.notifications.just_now;
  if (diff < 60) return `${Math.floor(diff)} ${t.notifications.minutes_ago}`;
  if (diff < 1440) return `${Math.floor(diff / 60)} ${t.notifications.hours_ago}`;
  return `${Math.floor(diff / 1440)} ${t.notifications.days_ago}`;
}

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUnread, setShowUnread] = useState(false);

  const fetchNotifications = useCallback(() => {
    setLoading(true);
    const query = showUnread ? '?unread_only=true' : '';
    apiGet(`/notifications${query}`)
      .then(setNotifications)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [showUnread]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await apiPatch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch (e) {
      console.error(e);
    }
  };

  const markAllRead = async () => {
    try {
      await apiPost('/notifications/mark-all-read');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (e) {
      console.error(e);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="space-y-5 fade-in">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1
            className="text-4xl font-bold text-zinc-50 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t.notifications.title}
          </h1>
          <p className="text-zinc-400 mt-1 text-sm">{t.notifications.subtitle}</p>
        </div>
        <button
          data-testid="mark-all-read-btn"
          onClick={markAllRead}
          disabled={unreadCount === 0}
          className="flex items-center gap-2 px-3 py-2 text-xs rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: '#18181B',
            border: '1px solid #27272A',
            color: unreadCount > 0 ? '#D4D4D8' : '#52525B',
          }}
        >
          <CheckCheck size={13} />
          {t.notifications.mark_all_read}
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <div
        className="flex rounded-sm overflow-hidden w-fit"
        style={{ border: '1px solid #27272A' }}
      >
        <button
          data-testid="filter-all-notifications"
          onClick={() => setShowUnread(false)}
          className={`px-4 py-2 text-sm transition-colors ${
            !showUnread
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
          }`}
        >
          {t.notifications.all}
        </button>
        <button
          data-testid="filter-unread-notifications"
          onClick={() => setShowUnread(true)}
          className={`px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
            showUnread
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
          }`}
        >
          {t.notifications.unread}
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 text-xs rounded-full bg-emerald-500/20 text-emerald-400">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* List */}
      <div
        className="rounded-sm overflow-hidden"
        style={{ background: '#18181B', border: '1px solid #27272A' }}
      >
        {loading ? (
          <div className="flex items-center gap-2 justify-center py-16 text-zinc-500">
            <Loader2 size={18} className="animate-spin" />
            <span>{t.common.loading}</span>
          </div>
        ) : error ? (
          <div className="text-red-400 text-center py-10">{t.common.error}</div>
        ) : notifications.length === 0 ? (
          <div className="text-zinc-600 text-center py-16 text-sm">
            <Bell size={32} className="mx-auto mb-3 opacity-30" />
            {t.notifications.no_notifications}
          </div>
        ) : (
          notifications.map((notif) => {
            const config = TYPE_CONFIG[notif.type] || TYPE_CONFIG.system;
            const Icon = config.icon;
            return (
              <div
                key={notif.id}
                data-testid={`notification-${notif.id}`}
                className="flex items-start gap-4 px-5 py-4 transition-colors"
                style={{
                  borderBottom: '1px solid #1F1F22',
                  background: notif.is_read ? 'transparent' : 'rgba(16,185,129,0.03)',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = 'rgba(39,39,42,0.4)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = notif.is_read
                    ? 'transparent'
                    : 'rgba(16,185,129,0.03)')
                }
              >
                {/* Type icon */}
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-sm flex items-center justify-center mt-0.5 ${config.bg}`}
                >
                  <Icon size={14} className={config.color} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-sm font-medium ${
                            notif.is_read ? 'text-zinc-400' : 'text-zinc-100'
                          }`}
                        >
                          {notif.title}
                        </span>
                        {!notif.is_read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                        )}
                        <span className={`px-1.5 py-0.5 text-[10px] rounded uppercase tracking-wider font-medium ${config.bg} ${config.color}`}>
                          {config.label}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 leading-relaxed">
                        {notif.message}
                      </div>
                      {notif.related_match_id && (
                        <Link
                          to={`/incontri/${notif.related_match_id}`}
                          data-testid={`notif-match-link-${notif.id}`}
                          className="inline-flex items-center gap-1 mt-1.5 text-xs text-emerald-500 hover:text-emerald-400 transition-colors"
                        >
                          <ExternalLink size={10} />
                          {t.common.view_details}
                        </Link>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className="text-xs text-zinc-600 whitespace-nowrap"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {timeAgo(notif.created_at)}
                      </span>
                      {!notif.is_read && (
                        <button
                          data-testid={`mark-read-${notif.id}`}
                          onClick={() => markRead(notif.id)}
                          className="w-7 h-7 rounded-sm flex items-center justify-center text-zinc-600 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors flex-shrink-0"
                          title={t.notifications.mark_read}
                        >
                          <Check size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
